import * as React from "react"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "./table"
import { cn } from "@/utils"

export interface DataTableColumn<T> {
  /** Unique key for this column */
  key: string
  /** Column header title */
  title: React.ReactNode
  /** Data index — property name on T to display */
  dataIndex?: keyof T
  /** Custom render function */
  render?: (value: any, record: T, index: number) => React.ReactNode
  /** Column width (CSS value) */
  width?: string | number
  /** Enable sorting */
  sorter?: boolean | ((a: T, b: T) => number)
  /** Text alignment */
  align?: "left" | "center" | "right"
  /** Additional className */
  className?: string
}

export interface DataTableProps<T> {
  /** Column definitions */
  columns: DataTableColumn<T>[]
  /** Data source */
  dataSource: T[]
  /** Unique key extractor */
  rowKey?: keyof T | ((record: T) => string)
  /** Loading state */
  loading?: boolean
  /** Empty state content */
  emptyText?: React.ReactNode
  /** Row selection config */
  rowSelection?: {
    type?: "checkbox" | "radio"
    selectedRowKeys?: (string | number)[]
    onChange?: (selectedKeys: (string | number)[], selectedRows: T[]) => void
  }
  /** Called when sort changes */
  onSort?: (key: string, direction: "asc" | "desc" | null) => void
  /** Additional className for the table wrapper */
  className?: string
  /** Row className */
  rowClassName?: string | ((record: T, index: number) => string)
}

function DataTable<T extends Record<string, any>>({
  columns,
  dataSource,
  rowKey = "id" as keyof T,
  loading,
  emptyText = "No data",
  rowSelection,
  onSort,
  className,
  rowClassName,
}: DataTableProps<T>) {
  const [sortState, setSortState] = React.useState<{
    key: string
    direction: "asc" | "desc" | null
  }>({ key: "", direction: null })

  const getRowKey = (record: T, index: number): string => {
    if (typeof rowKey === "function") return rowKey(record)
    return String(record[rowKey] ?? index)
  }

  const handleSort = (col: DataTableColumn<T>) => {
    if (!col.sorter) return
    let next: "asc" | "desc" | null
    if (sortState.key !== col.key) next = "asc"
    else if (sortState.direction === "asc") next = "desc"
    else if (sortState.direction === "desc") next = null
    else next = "asc"

    setSortState({ key: col.key, direction: next })
    onSort?.(col.key, next)
  }

  // Apply client-side sorting if sorter is a function
  const sortedData = React.useMemo(() => {
    if (!sortState.direction || !sortState.key) return dataSource
    const col = columns.find((c) => c.key === sortState.key)
    if (!col?.sorter || typeof col.sorter !== "function") return dataSource
    const sorted = [...dataSource].sort(col.sorter)
    return sortState.direction === "desc" ? sorted.reverse() : sorted
  }, [dataSource, sortState, columns])

  return (
    <div className={cn("relative", className)}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
          <svg className="animate-spin h-6 w-6 text-primary" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            {rowSelection && (
              <TableHead className="w-12" />
            )}
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  col.sorter && "cursor-pointer select-none",
                  col.align === "center" && "text-center",
                  col.align === "right" && "text-right",
                  col.className
                )}
                style={{ width: col.width }}
                onClick={() => col.sorter && handleSort(col)}
              >
                <span className="inline-flex items-center gap-1">
                  {col.title}
                  {col.sorter && sortState.key === col.key && sortState.direction && (
                    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="currentColor">
                      {sortState.direction === "asc" ? (
                        <path d="M6 2L10 8H2L6 2Z" />
                      ) : (
                        <path d="M6 10L2 4H10L6 10Z" />
                      )}
                    </svg>
                  )}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + (rowSelection ? 1 : 0)}
                className="h-24 text-center text-muted-foreground"
              >
                {emptyText}
              </TableCell>
            </TableRow>
          ) : (
            sortedData.map((record, idx) => {
              const key = getRowKey(record, idx)
              const isSelected = rowSelection?.selectedRowKeys?.includes(key)
              const rClassName =
                typeof rowClassName === "function"
                  ? rowClassName(record, idx)
                  : rowClassName

              return (
                <TableRow
                  key={key}
                  data-state={isSelected ? "selected" : undefined}
                  className={rClassName}
                >
                  {rowSelection && (
                    <TableCell className="w-12">
                      <input
                        type={rowSelection.type === "radio" ? "radio" : "checkbox"}
                        checked={isSelected}
                        onChange={() => {
                          if (!rowSelection.onChange) return
                          if (rowSelection.type === "radio") {
                            rowSelection.onChange([key], [record])
                          } else {
                            const keys = isSelected
                              ? (rowSelection.selectedRowKeys || []).filter((k) => k !== key)
                              : [...(rowSelection.selectedRowKeys || []), key]
                            const rows = sortedData.filter((r, i) =>
                              keys.includes(getRowKey(r, i))
                            )
                            rowSelection.onChange(keys, rows)
                          }
                        }}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
                      />
                    </TableCell>
                  )}
                  {columns.map((col) => {
                    const value = col.dataIndex ? record[col.dataIndex] : undefined
                    return (
                      <TableCell
                        key={col.key}
                        className={cn(
                          col.align === "center" && "text-center",
                          col.align === "right" && "text-right",
                          col.className
                        )}
                      >
                        {col.render ? col.render(value, record, idx) : value}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export { DataTable }
